import { Grid, Box } from "@material-ui/core"
import { useSnackbar } from "notistack"
import React, { FunctionComponent, useEffect } from "react"
import { useForm, useFieldArray, FormProvider } from "react-hook-form"
import slugify from "slugify"
import { useData } from "../common/DataProvider"
import { AddFields } from "./AddFields"
import { CommonFieldPropsContext } from "./CommonFieldPropsContext"
import { ImportExportSchemaButton } from "./ImportExportSchemaButton"
import { NoFields } from "./NoFields"
import { RenderSchemaField } from "./RenderSchemaField"
import { SaveSchemaButton } from "./SaveSchemaButton"
import { SchemaField, Schema } from "./schema"
import { SchemaFieldAccordion } from "./SchemaFieldAccordion"
import { useLayerSchema } from "./useLayerSchema"

/** SchemaEditor component props */
interface SchemaEditorProps {}

export const SchemaEditor: FunctionComponent<SchemaEditorProps> = () => {
  /** Grab the currently active feture layer */
  const {
    activeFeatureLayer: [featureLayer],
  } = useData()

  /** Get the schema if it exists */
  const { enqueueSnackbar } = useSnackbar()

  /** Handle form */
  const methods = useForm<{ fields: SchemaField[] }>()
  const { schema, saveSchema } = useLayerSchema(featureLayer)

  /** Set form to schema when it changes */
  const { reset } = methods
  useEffect(() => {
    if (schema) {
      reset({
        fields: schema.map(field => ({
          ...field,
          id: field.name,
        })),
      })
    }
  }, [schema, reset])

  /** Handle form submit */
  const handleFormSubmit = (values: any) => {
    saveSchema(values.fields)
      .then(() =>
        enqueueSnackbar("Schema updated successfully.", { variant: "success" })
      )
      .catch((error: Error) => {
        console.log(error)
        enqueueSnackbar("There was an error saving the schema", {
          variant: "error",
        })
      })
  }

  /** Get array fields */
  const { fields, remove, append } = useFieldArray<SchemaField>({
    control: methods.control,
    name: "fields",
  })

  /** Handle schema being imported */
  const handleImport = (importedSchema: Schema) => {
    reset(importedSchema)
    handleFormSubmit(importedSchema)
  }

  /** Sort fields by order */
  const sortFields = (fieldA: SchemaField, fieldB: SchemaField) => {
    // if no order specified, leave in place
    if (!fieldA.order && !fieldB.order) return 0
    // otherwise compare the fields
    // @ts-ignore - unreachable if .order is undefined
    return fieldA.order < fieldB.order ? -1 : 1
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={8}>
            {fields.length ? (
              fields.sort(sortFields).map((field, index) => (
                <CommonFieldPropsContext.Provider
                  value={{ field, index, remove, append }}
                  key={field.name || index}
                >
                  <SchemaFieldAccordion>
                    <RenderSchemaField />
                  </SchemaFieldAccordion>
                </CommonFieldPropsContext.Provider>
              ))
            ) : (
              <NoFields />
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box position="sticky" top={16}>
              <AddFields append={append} />
              <SaveSchemaButton loading={methods.formState.isSubmitting} />
              <ImportExportSchemaButton
                schema={methods.getValues()}
                schemaPrefix={featureLayer && slugify(featureLayer?.title)}
                onConfirmImport={handleImport}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
